import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { ArrowLeftIcon, LockIcon, UnlockIcon, ShieldAlertIcon, KeyIcon, MapPinIcon, UserIcon, Trash2Icon, PencilIcon, StickyNoteIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import MapView from '../components/MapView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DeviceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [locations, setLocations] = useState([]);
  const [events, setEvents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unlockCode, setUnlockCode] = useState(null);
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [showBiosPassword, setShowBiosPassword] = useState(false);
  const [editForm, setEditForm] = useState({ 
    model: '', 
    serialNumber: '', 
    biosSerial: '',
    os: '', 
    notes: '',
    biosPassword: '',
    windowsLicenseKey: '',
    officeLicenseKey: ''
  });

  useEffect(() => {
    fetchDeviceData();
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchDeviceData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [deviceRes, locationsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/admin/devices/${id}`, { headers }),
        axios.get(`${API}/admin/devices/${id}/locations`, { headers }),
        axios.get(`${API}/admin/devices/${id}/events`, { headers })
      ]);
      
      setDevice(deviceRes.data);
      setLocations(locationsRes.data);
      setEvents(eventsRes.data);
      setSelectedCustomerId(deviceRes.data.assignedCustomerId || '');
      setEditForm({
        model: deviceRes.data.model || '',
        serialNumber: deviceRes.data.serialNumber || '',
        biosSerial: deviceRes.data.biosSerial || '',
        os: deviceRes.data.os || '',
        notes: deviceRes.data.notes || '',
        biosPassword: deviceRes.data.biosPassword || '',
        windowsLicenseKey: deviceRes.data.windowsLicenseKey || '',
        officeLicenseKey: deviceRes.data.officeLicenseKey || ''
      });
    } catch (error) {
      toast.error('Failed to load device details');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to load customers');
    }
  };

  const handleLock = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/devices/${id}/lock`, 
        { reason: 'Manual lock by admin' },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Device locked successfully');
      fetchDeviceData();
    } catch (error) {
      toast.error('Failed to lock device');
    }
  };

  const handleUnlock = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/devices/${id}/unlock`, {}, 
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Device unlocked successfully');
      fetchDeviceData();
    } catch (error) {
      toast.error('Failed to unlock device');
    }
  };

  const handleSetStolen = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/devices/${id}/set-stolen`, {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Device marked as stolen');
      fetchDeviceData();
    } catch (error) {
      toast.error('Failed to mark device as stolen');
    }
  };

  const handleAssignCustomer = async (customerId) => {
    const actualCustomerId = customerId === 'none' ? null : customerId;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(`${API}/admin/devices/${id}/customer`,
        { customerId: actualCustomerId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(actualCustomerId ? 'Customer assigned successfully' : 'Customer unassigned');
      setSelectedCustomerId(customerId);
      fetchDeviceData();
    } catch (error) {
      toast.error('Failed to assign customer');
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/devices/${id}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Device permanently deleted');
      navigate('/devices');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete device';
      toast.error(message);
    }
  };

  const handleEditDevice = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/devices/${id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Device updated successfully');
      setShowEditDialog(false);
      fetchDeviceData();
    } catch (error) {
      toast.error('Failed to update device');
    }
  };

  const handleGenerateCode = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/devices/${id}/generate-unlock-code`,
        { expiryHours: 24 },
        { headers: { Authorization: `Bearer ${token}` }});
      setUnlockCode(response.data);
      setShowUnlockDialog(true);
      toast.success('Unlock code generated');
    } catch (error) {
      toast.error('Failed to generate unlock code');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: 'bg-green-100 text-green-800',
      LOCKED: 'bg-orange-100 text-orange-800',
      STOLEN: 'bg-red-100 text-red-800',
      OVERDUE: 'bg-yellow-100 text-yellow-800',
      SECURITY_ALERT: 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  if (!device) {
    return <div className="text-center py-12">Device not found</div>;
  }

  return (
    <div data-testid="device-detail-page">
      <Button variant="ghost" onClick={() => navigate('/devices')} className="mb-4" data-testid="back-to-devices-button">
        <ArrowLeftIcon className="h-4 w-4 mr-2" />
        Back to Devices
      </Button>

      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">{device.model}</h1>
            <p className="text-gray-600 mt-2">Serial: {device.serialNumber}</p>
          </div>
          <Badge className={`${getStatusColor(device.status)} text-lg px-4 py-2`} data-testid="device-detail-status">
            {device.status}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {device.status === 'ACTIVE' ? (
              <Button onClick={handleLock} className="w-full" variant="destructive" data-testid="lock-device-button">
                <LockIcon className="h-4 w-4 mr-2" />
                Lock Device
              </Button>
            ) : (
              <Button onClick={handleUnlock} className="w-full" data-testid="unlock-device-button">
                <UnlockIcon className="h-4 w-4 mr-2" />
                Unlock Device
              </Button>
            )}
            <Button onClick={handleSetStolen} className="w-full" variant="destructive" data-testid="mark-stolen-button">
              <ShieldAlertIcon className="h-4 w-4 mr-2" />
              Mark as Stolen
            </Button>
            <Button onClick={handleGenerateCode} className="w-full" variant="outline" data-testid="generate-code-button">
              <KeyIcon className="h-4 w-4 mr-2" />
              Generate Unlock Code
            </Button>
            <Button onClick={() => setShowEditDialog(true)} className="w-full" variant="outline" data-testid="edit-device-button">
              <PencilIcon className="h-4 w-4 mr-2" />
              Edit Device
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button className="w-full" variant="destructive" data-testid="delete-device-button">
                  <Trash2Icon className="h-4 w-4 mr-2" />
                  Delete Device
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Device Permanently?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete <strong>{device.model}</strong> and all associated data including location logs, events, and unlock codes. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleDelete}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="confirm-delete-device"
                  >
                    Delete Permanently
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            
            {/* Customer Assignment */}
            <div className="pt-3 border-t">
              <p className="text-sm text-gray-500 mb-2 flex items-center">
                <UserIcon className="h-4 w-4 mr-1" />
                Assign to Customer
              </p>
              <Select value={selectedCustomerId} onValueChange={handleAssignCustomer} data-testid="customer-select">
                <SelectTrigger className="w-full" data-testid="customer-select-trigger">
                  <SelectValue placeholder="Select a customer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="customer-option-none">-- No Customer --</SelectItem>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id} data-testid={`customer-option-${customer.id}`}>
                      {customer.name} {customer.email ? `(${customer.email})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {device.assignedCustomerId && (
                <p className="text-xs text-green-600 mt-1">
                  Currently assigned to: {customers.find(c => c.id === device.assignedCustomerId)?.name || 'Unknown'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Device Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Operating System</p>
                <p className="font-medium">{device.os}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Health Score</p>
                <p className="font-medium">{device.healthScore}/100</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Security Status</p>
                <p className="font-medium">{device.securityStatus}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Last Seen</p>
                <p className="font-medium">
                  {device.lastSeenAt ? new Date(device.lastSeenAt).toLocaleString() : 'Never'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Last IP</p>
                <p className="font-medium">{device.lastIp || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">City</p>
                <p className="font-medium">{device.city || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Created At</p>
                <p className="font-medium">{new Date(device.createdAt).toLocaleDateString()}</p>
              </div>
            </div>
            
            {/* BIOS Serial - Primary Identifier */}
            {device.biosSerial && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-gray-500">BIOS Serial (Primary ID)</p>
                <p className="font-mono text-sm font-medium" data-testid="device-bios-serial">{device.biosSerial}</p>
              </div>
            )}
            
            {/* Admin-Managed License Keys */}
            {(device.windowsLicenseKey || device.officeLicenseKey || device.biosPassword) && (
              <div className="mt-4 pt-4 border-t bg-amber-50 -mx-6 -mb-6 px-6 py-4 rounded-b-lg">
                <p className="text-sm font-semibold text-amber-800 mb-3">Admin-Managed Information</p>
                <div className="grid grid-cols-1 gap-3">
                  {device.biosPassword && (
                    <div>
                      <p className="text-xs text-amber-700">BIOS Password</p>
                      <p className="font-mono text-sm" data-testid="device-bios-password">••••••••</p>
                    </div>
                  )}
                  {device.windowsLicenseKey && (
                    <div>
                      <p className="text-xs text-amber-700">Windows License Key</p>
                      <p className="font-mono text-sm" data-testid="device-windows-license">{device.windowsLicenseKey}</p>
                    </div>
                  )}
                  {device.officeLicenseKey && (
                    <div>
                      <p className="text-xs text-amber-700">Office License Key</p>
                      <p className="font-mono text-sm" data-testid="device-office-license">{device.officeLicenseKey}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {device.notes && (
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <StickyNoteIcon className="h-4 w-4 mr-1" />
                  Notes
                </div>
                <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded">{device.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Hardware Information Section */}
      {(device.ramGb || device.cpuModel || device.storageConfig) && (
        <Card className="mb-6" data-testid="hardware-info-card">
          <CardHeader>
            <CardTitle>Hardware Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {device.ramGb && (
                <div>
                  <p className="text-sm text-gray-500">RAM</p>
                  <p className="font-medium" data-testid="device-ram">{device.ramGb} GB</p>
                </div>
              )}
              {device.cpuModel && (
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">CPU</p>
                  <p className="font-medium" data-testid="device-cpu">{device.cpuModel}</p>
                  {(device.cpuCores || device.cpuThreads) && (
                    <p className="text-xs text-gray-400">
                      {device.cpuCores && `${device.cpuCores} cores`}
                      {device.cpuCores && device.cpuThreads && ' / '}
                      {device.cpuThreads && `${device.cpuThreads} threads`}
                    </p>
                  )}
                </div>
              )}
              {device.manufacturer && (
                <div>
                  <p className="text-sm text-gray-500">Manufacturer</p>
                  <p className="font-medium" data-testid="device-manufacturer">{device.manufacturer}</p>
                </div>
              )}
            </div>
            
            {/* Serial Numbers Section */}
            {device.hardwareInfo && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm font-semibold text-gray-700 mb-3">Serial Numbers & IDs</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-gray-50 p-4 rounded-lg">
                  {device.hardwareInfo.biosSerial && (
                    <div>
                      <p className="text-xs text-gray-500">BIOS Serial</p>
                      <p className="font-mono text-sm" data-testid="bios-serial">{device.hardwareInfo.biosSerial}</p>
                    </div>
                  )}
                  {device.hardwareInfo.motherboardSerial && (
                    <div>
                      <p className="text-xs text-gray-500">Motherboard Serial</p>
                      <p className="font-mono text-sm" data-testid="motherboard-serial">{device.hardwareInfo.motherboardSerial}</p>
                    </div>
                  )}
                  {device.hardwareInfo.systemSerial && (
                    <div>
                      <p className="text-xs text-gray-500">System Serial</p>
                      <p className="font-mono text-sm" data-testid="system-serial">{device.hardwareInfo.systemSerial}</p>
                    </div>
                  )}
                  {device.hardwareInfo.systemUuid && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500">System UUID</p>
                      <p className="font-mono text-xs" data-testid="system-uuid">{device.hardwareInfo.systemUuid}</p>
                    </div>
                  )}
                  {device.hardwareInfo.cpu?.processorId && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500">Processor ID</p>
                      <p className="font-mono text-xs" data-testid="processor-id">{device.hardwareInfo.cpu.processorId}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* OS Information */}
            {device.hardwareInfo?.os && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm font-semibold text-gray-700 mb-3">Operating System</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {device.hardwareInfo.os.name && (
                    <div>
                      <p className="text-xs text-gray-500">OS</p>
                      <p className="font-medium text-sm">{device.hardwareInfo.os.name}</p>
                    </div>
                  )}
                  {device.hardwareInfo.os.release && (
                    <div>
                      <p className="text-xs text-gray-500">Version</p>
                      <p className="font-medium text-sm">{device.hardwareInfo.os.release}</p>
                    </div>
                  )}
                  {device.hardwareInfo.os.arch && (
                    <div>
                      <p className="text-xs text-gray-500">Architecture</p>
                      <p className="font-medium text-sm">{device.hardwareInfo.os.arch}</p>
                    </div>
                  )}
                  {device.hardwareInfo.os.nodeName && (
                    <div>
                      <p className="text-xs text-gray-500">Computer Name</p>
                      <p className="font-medium text-sm">{device.hardwareInfo.os.nodeName}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {device.storageConfig && device.storageConfig.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm font-semibold text-gray-700 mb-3">Storage</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {device.storageConfig.map((storage, idx) => (
                    <div key={idx} className="bg-gray-50 p-3 rounded text-sm" data-testid={`storage-${idx}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{storage.type || 'Disk'}</span>
                        <span className="text-gray-600">{storage.sizeGb} GB</span>
                      </div>
                      {storage.model && <p className="text-xs text-gray-500 mt-1">{storage.model}</p>}
                      {storage.serialNumber && <p className="text-xs text-gray-400 font-mono mt-1">S/N: {storage.serialNumber}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Network Adapters */}
            {device.hardwareInfo?.networkAdapters && device.hardwareInfo.networkAdapters.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm font-semibold text-gray-700 mb-3">Network Adapters</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {device.hardwareInfo.networkAdapters.map((adapter, idx) => (
                    <div key={idx} className="bg-gray-50 p-2 rounded text-sm">
                      <p className="text-xs text-gray-500">{adapter.interface}</p>
                      <p className="font-mono text-xs">{adapter.mac}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Location Details Section - Shows unified lastLocation data */}
      {device.lastLocation && (
        <Card className="mb-6" data-testid="location-details-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <MapPinIcon className="h-5 w-5 mr-2" />
              Location Details
            </CardTitle>
            {device.lastLocation.updatedAt && (
              <p className="text-xs text-gray-500">
                Updated: {new Date(device.lastLocation.updatedAt).toLocaleString()}
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {/* IP Geolocation */}
            {device.lastLocation.ipGeo && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  IP Geolocation
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-blue-50 p-4 rounded-lg">
                  {device.lastLocation.ipGeo.publicIp && (
                    <div>
                      <p className="text-xs text-gray-500">Public IP</p>
                      <p className="font-medium font-mono text-sm" data-testid="loc-public-ip">{device.lastLocation.ipGeo.publicIp}</p>
                    </div>
                  )}
                  {device.lastLocation.ipGeo.city && (
                    <div>
                      <p className="text-xs text-gray-500">City</p>
                      <p className="font-medium" data-testid="loc-city">{device.lastLocation.ipGeo.city}</p>
                    </div>
                  )}
                  {device.lastLocation.ipGeo.region && (
                    <div>
                      <p className="text-xs text-gray-500">Region</p>
                      <p className="font-medium" data-testid="loc-region">{device.lastLocation.ipGeo.region}</p>
                    </div>
                  )}
                  {device.lastLocation.ipGeo.country && (
                    <div>
                      <p className="text-xs text-gray-500">Country</p>
                      <p className="font-medium" data-testid="loc-country">{device.lastLocation.ipGeo.country}</p>
                    </div>
                  )}
                  {device.lastLocation.ipGeo.isp && (
                    <div>
                      <p className="text-xs text-gray-500">ISP</p>
                      <p className="font-medium" data-testid="loc-isp">{device.lastLocation.ipGeo.isp}</p>
                    </div>
                  )}
                  {device.lastLocation.ipGeo.lat && device.lastLocation.ipGeo.lng && (
                    <div>
                      <p className="text-xs text-gray-500">Coordinates</p>
                      <p className="font-medium font-mono text-sm" data-testid="loc-coords">
                        {device.lastLocation.ipGeo.lat.toFixed(4)}, {device.lastLocation.ipGeo.lng.toFixed(4)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Network Info */}
            {device.lastLocation.networkInfo && Object.keys(device.lastLocation.networkInfo).length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Wi-Fi / Network Information
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-green-50 p-4 rounded-lg">
                  {device.lastLocation.networkInfo.ssid && (
                    <div>
                      <p className="text-xs text-gray-500">Wi-Fi Network (SSID)</p>
                      <p className="font-medium" data-testid="loc-ssid">{device.lastLocation.networkInfo.ssid}</p>
                    </div>
                  )}
                  {device.lastLocation.networkInfo.bssid && (
                    <div>
                      <p className="text-xs text-gray-500">Access Point (BSSID)</p>
                      <p className="font-medium font-mono text-xs" data-testid="loc-bssid">{device.lastLocation.networkInfo.bssid}</p>
                    </div>
                  )}
                  {device.lastLocation.networkInfo.localIp && (
                    <div>
                      <p className="text-xs text-gray-500">Local IP</p>
                      <p className="font-medium font-mono text-sm" data-testid="loc-local-ip">{device.lastLocation.networkInfo.localIp}</p>
                    </div>
                  )}
                  {device.lastLocation.networkInfo.gatewayIp && (
                    <div>
                      <p className="text-xs text-gray-500">Gateway</p>
                      <p className="font-medium font-mono text-sm" data-testid="loc-gateway">{device.lastLocation.networkInfo.gatewayIp}</p>
                    </div>
                  )}
                  {device.lastLocation.networkInfo.signalStrength && (
                    <div>
                      <p className="text-xs text-gray-500">Signal Strength</p>
                      <p className="font-medium" data-testid="loc-signal">{device.lastLocation.networkInfo.signalStrength}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* OS Location */}
            {device.lastLocation.osLocation && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                  OS Location (High Accuracy)
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-purple-50 p-4 rounded-lg">
                  {device.lastLocation.osLocation.lat && device.lastLocation.osLocation.lng && (
                    <div>
                      <p className="text-xs text-gray-500">Coordinates</p>
                      <p className="font-medium font-mono text-sm" data-testid="loc-os-coords">
                        {device.lastLocation.osLocation.lat.toFixed(6)}, {device.lastLocation.osLocation.lng.toFixed(6)}
                      </p>
                    </div>
                  )}
                  {device.lastLocation.osLocation.accuracyMeters && (
                    <div>
                      <p className="text-xs text-gray-500">Accuracy</p>
                      <p className="font-medium" data-testid="loc-os-accuracy">±{device.lastLocation.osLocation.accuracyMeters}m</p>
                    </div>
                  )}
                  {device.lastLocation.osLocation.source && (
                    <div>
                      <p className="text-xs text-gray-500">Source</p>
                      <p className="font-medium" data-testid="loc-os-source">{device.lastLocation.osLocation.source}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Legacy Network Info (fallback for older devices) */}
      {!device.lastLocation && device.lastNetworkInfo && Object.keys(device.lastNetworkInfo).length > 0 && (
        <Card className="mb-6" data-testid="network-info-card">
          <CardHeader>
            <CardTitle>Last Network Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {device.lastNetworkInfo.ssid && (
                <div>
                  <p className="text-sm text-gray-500">Wi-Fi Network</p>
                  <p className="font-medium" data-testid="network-ssid">{device.lastNetworkInfo.ssid}</p>
                </div>
              )}
              {device.lastNetworkInfo.bssid && (
                <div>
                  <p className="text-sm text-gray-500">BSSID</p>
                  <p className="font-medium font-mono text-xs" data-testid="network-bssid">{device.lastNetworkInfo.bssid}</p>
                </div>
              )}
              {device.lastNetworkInfo.localIp && (
                <div>
                  <p className="text-sm text-gray-500">Local IP</p>
                  <p className="font-medium" data-testid="network-local-ip">{device.lastNetworkInfo.localIp}</p>
                </div>
              )}
              {device.lastNetworkInfo.gatewayIp && (
                <div>
                  <p className="text-sm text-gray-500">Gateway</p>
                  <p className="font-medium" data-testid="network-gateway">{device.lastNetworkInfo.gatewayIp}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Map View */}
      {(device.lastLocationLat && device.lastLocationLng) || (device.lastLocation?.ipGeo?.lat && device.lastLocation?.ipGeo?.lng) ? (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <MapPinIcon className="h-5 w-5 mr-2" />
              Map View
            </CardTitle>
          </CardHeader>
          <CardContent>
            <MapView 
              lat={device.lastLocationLat || device.lastLocation?.ipGeo?.lat} 
              lng={device.lastLocationLng || device.lastLocation?.ipGeo?.lng} 
            />
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Recent Events</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {events.slice(0, 10).map((event) => (
              <div key={event.id} className="border-l-4 border-primary pl-4 py-2" data-testid={`event-${event.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{event.type}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(event.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            {events.length === 0 && (
              <p className="text-gray-500 text-center py-4">No events recorded</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={showUnlockDialog} onOpenChange={setShowUnlockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unlock Code Generated</DialogTitle>
            <DialogDescription>
              Share this code with the user to unlock the device. It will expire in 24 hours.
            </DialogDescription>
          </DialogHeader>
          {unlockCode && (
            <div className="bg-gray-100 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">Unlock Code:</p>
              <p className="text-2xl font-mono font-bold text-center select-all" data-testid="generated-unlock-code">{unlockCode.code}</p>
              <p className="text-xs text-gray-500 mt-3 text-center">
                Expires: {new Date(unlockCode.expiresAt).toLocaleString()}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Device Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Device</DialogTitle>
            <DialogDescription>Update device information, licenses, and notes</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditDevice} className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-model">Model</Label>
                <Input
                  id="edit-model"
                  value={editForm.model}
                  onChange={(e) => setEditForm({...editForm, model: e.target.value})}
                  data-testid="edit-device-model"
                />
              </div>
              <div>
                <Label htmlFor="edit-os">Operating System</Label>
                <Input
                  id="edit-os"
                  value={editForm.os}
                  onChange={(e) => setEditForm({...editForm, os: e.target.value})}
                  data-testid="edit-device-os"
                />
              </div>
            </div>
            
            {/* Serial Numbers */}
            <div className="border-t pt-4">
              <p className="text-sm font-semibold text-gray-700 mb-3">Serial Numbers</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-serial">Serial Number (Display)</Label>
                  <Input
                    id="edit-serial"
                    value={editForm.serialNumber}
                    onChange={(e) => setEditForm({...editForm, serialNumber: e.target.value})}
                    data-testid="edit-device-serial"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-bios-serial">BIOS Serial (Source)</Label>
                  <Input
                    id="edit-bios-serial"
                    value={editForm.biosSerial}
                    onChange={(e) => setEditForm({...editForm, biosSerial: e.target.value})}
                    placeholder="From BIOS"
                    data-testid="edit-device-bios-serial"
                  />
                </div>
              </div>
            </div>
            
            {/* Admin-Managed Fields */}
            <div className="border-t pt-4">
              <p className="text-sm font-semibold text-gray-700 mb-3">License Keys & Passwords (Admin Only)</p>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="edit-bios-password">BIOS Password</Label>
                  <div className="relative">
                    <Input
                      id="edit-bios-password"
                      type={showBiosPassword ? "text" : "password"}
                      value={editForm.biosPassword}
                      onChange={(e) => setEditForm({...editForm, biosPassword: e.target.value})}
                      placeholder="Enter BIOS password"
                      data-testid="edit-device-bios-password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                      onClick={() => setShowBiosPassword(!showBiosPassword)}
                    >
                      {showBiosPassword ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="edit-windows-license">Windows License Key</Label>
                  <Input
                    id="edit-windows-license"
                    value={editForm.windowsLicenseKey}
                    onChange={(e) => setEditForm({...editForm, windowsLicenseKey: e.target.value})}
                    placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"
                    className="font-mono"
                    data-testid="edit-device-windows-license"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-office-license">Office License Key</Label>
                  <Input
                    id="edit-office-license"
                    value={editForm.officeLicenseKey}
                    onChange={(e) => setEditForm({...editForm, officeLicenseKey: e.target.value})}
                    placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"
                    className="font-mono"
                    data-testid="edit-device-office-license"
                  />
                </div>
              </div>
            </div>
            
            {/* Notes */}
            <div className="border-t pt-4">
              <Label htmlFor="edit-notes">Notes</Label>
              <Textarea
                id="edit-notes"
                value={editForm.notes}
                onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                placeholder="Add notes about this device..."
                rows={3}
                data-testid="edit-device-notes"
              />
            </div>
            
            <Button type="submit" className="w-full" data-testid="save-device-button">
              Save Changes
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}